import * as crypto from 'crypto';

export interface CacheOptions {
  ttl: number; // Time to live in milliseconds
  maxSize?: number; // Maximum number of entries
  updateOnGet?: boolean; // Reset TTL on get
  onEvict?: (key: string, value: any) => void; // Callback on eviction
}

interface CacheEntry<T> {
  value: T;
  expiry: number;
  size: number;
  hits: number;
  lastAccess: number;
}

export interface CacheStats {
  entries: number;
  hits: number;
  misses: number;
  evictions: number;
  size: number;
}

export class CacheManager {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private stats: CacheStats = {
    entries: 0,
    hits: 0,
    misses: 0,
    evictions: 0,
    size: 0,
  };
  private cleanupInterval?: NodeJS.Timeout;
  private defaultOptions: Partial<CacheOptions> = {
    ttl: 5 * 60 * 1000, // 5 minutes default
    maxSize: 1000,
    updateOnGet: false,
  };

  constructor(options?: Partial<CacheOptions>) {
    this.defaultOptions = { ...this.defaultOptions, ...options };
    this.startCleanupInterval();
  }

  /**
   * Set a value in the cache
   */
  set<T>(key: string, value: T, options?: Partial<CacheOptions>): void {
    const opts = { ...this.defaultOptions, ...options } as CacheOptions;
    const expiry = Date.now() + opts.ttl;
    const size = this.estimateSize(value);

    // Check if we need to evict entries
    if (opts.maxSize && this.cache.size >= opts.maxSize) {
      this.evictLRU();
    }

    const entry: CacheEntry<T> = {
      value,
      expiry,
      size,
      hits: 0,
      lastAccess: Date.now(),
    };

    this.cache.set(key, entry);
    this.stats.entries = this.cache.size;
    this.stats.size += size;
  }

  /**
   * Get a value from the cache
   */
  get<T>(key: string, options?: { updateOnGet?: boolean }): T | undefined {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.misses++;
      return undefined;
    }
    
    if (Date.now() > entry.expiry) {
      this.delete(key);
      this.stats.misses++;
      return undefined;
    }

    // Update stats
    entry.hits++;
    entry.lastAccess = Date.now();
    this.stats.hits++;

    // Update expiry if requested
    if (options?.updateOnGet ?? this.defaultOptions.updateOnGet) {
      entry.expiry = Date.now() + (this.defaultOptions.ttl || 5 * 60 * 1000);
    }
    
    return entry.value;
  }

  /**
   * Get or set a value (memoization helper)
   */
  async getOrSet<T>(
    key: string,
    factory: () => Promise<T>,
    options?: Partial<CacheOptions>
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== undefined) {
      return cached;
    }

    const value = await factory();
    this.set(key, value, options);
    return value;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    
    if (Date.now() > entry.expiry) {
      this.delete(key);
      return false;
    }
    
    return true;
  }

  /**
   * Delete a key from the cache
   */
  delete(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    this.stats.size -= entry.size;
    this.stats.entries--;
    
    const deleted = this.cache.delete(key);
    
    if (deleted && this.defaultOptions.onEvict) {
      this.defaultOptions.onEvict(key, entry.value);
    }
    
    return deleted;
  }

  /**
   * Clear all entries
   */
  clear(): void {
    if (this.defaultOptions.onEvict) {
      for (const [key, entry] of this.cache.entries()) {
        this.defaultOptions.onEvict(key, entry.value);
      }
    }
    
    this.cache.clear();
    this.stats.entries = 0;
    this.stats.size = 0;
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Get all keys
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Create a cache key from multiple parts
   */
  static createKey(...parts: any[]): string {
    const str = parts.map(p => JSON.stringify(p)).join(':');
    return crypto.createHash('md5').update(str).digest('hex');
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    let evicted = 0;
    
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiry) {
        this.delete(key);
        evicted++;
      }
    }
    
    this.stats.evictions += evicted;
  }

  /**
   * Evict least recently used entry
   */
  private evictLRU(): void {
    let lruKey: string | null = null;
    let lruTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccess < lruTime) {
        lruTime = entry.lastAccess;
        lruKey = key;
      }
    }

    if (lruKey) {
      this.delete(lruKey);
      this.stats.evictions++;
    }
  }

  /**
   * Estimate size of a value in bytes
   */
  private estimateSize(value: any): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'string') return value.length * 2; // UTF-16
    if (typeof value === 'number') return 8;
    if (typeof value === 'boolean') return 4;
    if (value instanceof Date) return 8;
    if (Buffer.isBuffer(value)) return value.length;
    
    // For objects/arrays, use JSON stringify as approximation
    try {
      return JSON.stringify(value).length * 2;
    } catch {
      return 1024; // Default size for complex objects
    }
  }

  /**
   * Start automatic cleanup interval
   */
  private startCleanupInterval(): void {
    // Clean up every minute
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 60 * 1000);
  }

  /**
   * Stop the cache manager and cleanup
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.clear();
  }
}